import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Space, message, Popconfirm, Switch, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, KeyOutlined } from '@ant-design/icons';
import { userAPI, roleAPI } from '../services/api';
import { brand, gray, state, modalWidth } from '../theme/tokens';
import { EmptyState } from '../components/common';

interface RoleItem {
  id: number;
  role_code: string;
  role_name: string;
  is_system: boolean;
}

interface UserItem {
  id: number;
  username: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
  roles: RoleItem[];
}

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserItem | null>(null);
  const [form] = Form.useForm();

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserItem | null>(null);
  const [resetForm] = Form.useForm();

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([userAPI.list(), roleAPI.list()]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch {
      message.error('데이터를 불러오지 못했습니다.');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditUser(null);
    form.resetFields();
    form.setFieldsValue({ role_ids: [] });
    setModalOpen(true);
  };

  const openEdit = (user: UserItem) => {
    setEditUser(user);
    form.setFieldsValue({
      username: user.username,
      full_name: user.full_name,
      is_active: user.is_active,
      role_ids: user.roles.map(r => r.id),
      password: '',
    });
    setModalOpen(true);
  };

  const openResetPassword = (user: UserItem) => {
    setResetTarget(user);
    resetForm.resetFields();
    setResetModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editUser) {
        const data: any = { full_name: values.full_name, is_active: values.is_active, role_ids: values.role_ids };
        if (values.password) data.password = values.password;
        await userAPI.update(editUser.id, data);
        message.success('사용자가 수정되었습니다.');
      } else {
        await userAPI.create({
          username: values.username,
          password: values.password,
          full_name: values.full_name,
          role_ids: values.role_ids || [],
        });
        message.success('사용자가 생성되었습니다.');
      }
      setModalOpen(false);
      loadData();
    } catch (err: any) {
      if (err?.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const handleResetPassword = async () => {
    try {
      const values = await resetForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        message.error('새 비밀번호가 일치하지 않습니다.');
        return;
      }
      await userAPI.resetPassword(resetTarget!.id, { new_password: values.new_password });
      message.success(`${resetTarget!.username} 비밀번호가 초기화되었습니다.`);
      setResetModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '비밀번호 초기화 실패');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await userAPI.delete(id);
      message.success('사용자가 삭제되었습니다.');
      loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '삭제 실패');
    }
  };

  const columns = [
    {
      title: 'ID', dataIndex: 'id', width: 60,
    },
    {
      title: '사용자명', dataIndex: 'username', width: 130,
      render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: '이름', dataIndex: 'full_name', width: 120,
    },
    {
      title: '역할', dataIndex: 'roles', width: 200,
      render: (roles: RoleItem[]) => (
        <Space size={4} wrap>
          {roles.map(r => (
            <Tag key={r.id} color={r.is_system ? 'blue' : 'green'}>{r.role_name}</Tag>
          ))}
          {roles.length === 0 && <Tag color="default">미할당</Tag>}
        </Space>
      ),
    },
    {
      title: '상태', dataIndex: 'is_active', width: 80,
      render: (v: boolean) => <Tag color={v ? 'success' : 'error'}>{v ? '활성' : '비활성'}</Tag>,
    },
    {
      title: '생성일', dataIndex: 'created_at', width: 120,
      render: (v: string) => v ? new Date(v).toLocaleDateString('ko-KR') : '',
    },
    {
      title: '관리', width: 120,
      render: (_: any, record: UserItem) => (
        <Space size={4}>
          <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} /></Tooltip>
          <Tooltip title="비밀번호 초기화">
            <Button size="small" type="text" icon={<KeyOutlined />} onClick={() => openResetPassword(record)} />
          </Tooltip>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)} okText="삭제" cancelText="취소">
            <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<span><UserOutlined style={{ marginRight: 8 }} />사용자 관리</span>}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>사용자 추가</Button>}
    >
      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        locale={{ emptyText: <EmptyState title="등록된 사용자가 없습니다" size="sm" /> }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title={editUser ? '사용자 수정' : '사용자 추가'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editUser ? '수정' : '생성'}
        width={modalWidth.sm}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="username" label="사용자명" rules={[{ required: !editUser, message: '사용자명을 입력하세요' }]}>
            <Input disabled={!!editUser} placeholder="영문 소문자" />
          </Form.Item>
          <Form.Item name="password" label={editUser ? '비밀번호 (변경 시에만 입력)' : '비밀번호'}
            rules={editUser ? [] : [{ required: true, message: '비밀번호를 입력하세요' }]}>
            <Input.Password placeholder="비밀번호" />
          </Form.Item>
          <Form.Item name="full_name" label="이름">
            <Input placeholder="홍길동" />
          </Form.Item>
          <Form.Item name="role_ids" label="역할">
            <Select mode="multiple" placeholder="역할을 선택하세요" optionFilterProp="label"
              options={roles.map(r => ({ value: r.id, label: r.role_name }))}
            />
          </Form.Item>
          {editUser && (
            <Form.Item name="is_active" label="활성 상태" valuePropName="checked">
              <Switch checkedChildren="활성" unCheckedChildren="비활성" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={<><KeyOutlined style={{ marginRight: 8, color: state.warning }} />비밀번호 초기화 — {resetTarget?.username}</>}
        open={resetModalOpen}
        onCancel={() => setResetModalOpen(false)}
        onOk={handleResetPassword}
        okText="초기화"
        okButtonProps={{ danger: true }}
        width={modalWidth.xs}
        maskClosable={false}
      >
        <Form form={resetForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="new_password" label="새 비밀번호"
            rules={[{ required: true, message: '새 비밀번호를 입력하세요' }, { min: 4, message: '4자 이상 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 (4자 이상)" />
          </Form.Item>
          <Form.Item name="confirm_password" label="새 비밀번호 확인"
            rules={[{ required: true, message: '비밀번호를 다시 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 확인" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default UserManagementPage;
